import { ageBandFor } from '@digital-twins/shared-types'

import {
  asFiniteNumber,
  continuousBinEdges,
  continuousBinLabels,
  isInContinuousBin,
  readContinuousDomain,
} from '../utils/continuousBinning'

/**
 * The fields a grouped chart's segment question may declare. `demographicAttribute`
 * is stamped by the report from the typed demographic declaration
 * (`declaredDemographicDimensionId`); `_canonical.response` is stamped by the
 * compiler on a SurveyJS element.
 */
export interface SegmentGroupQuestion {
  name?: string
  title?: string
  demographicAttribute?: string | null
  _canonical?: { response?: { kind?: string } | null } | null
}

/**
 * How a grouped chart keys one segment value, from declared semantics only:
 *
 * - a population attribute declared age bins numeric values into the declared
 *   age bands (`ageBandFor`);
 * - a question the compiler declared continuous bins numeric values with the one
 *   continuous binning policy (`continuousBinEdges`, parity-tested against
 *   `tests/fixtures/continuous-binning/parity.v1.json`), over this segment's own
 *   values;
 * - anything else keys on the raw value.
 *
 * Never from the question's name or title: a field named "stage", "page" or
 * "language" is not an age, and an age field named "respondent_years" is one
 * when it is declared so.
 */
export function buildSegmentKeyer(
  groupQuestion: SegmentGroupQuestion | null | undefined,
  segmentValues: Array<unknown>,
): (value: unknown) => string {
  if (groupQuestion?.demographicAttribute === 'age') {
    return (value) => {
      const numeric = asFiniteNumber(value)
      return numeric === null ? String(value) : ageBandFor(numeric)
    }
  }

  const domain = readContinuousDomain(groupQuestion)
  if (domain) {
    const numericValues = segmentValues
      .map((value) => asFiniteNumber(value))
      .filter((value): value is number => value !== null)
      .sort((a, b) => a - b)
    const binning = continuousBinEdges(numericValues, domain)
    if (binning.edges.length > 0) {
      const labels = continuousBinLabels(binning)
      const lastIndex = binning.edges.length - 1
      return (value) => {
        const numeric = asFiniteNumber(value)
        if (numeric === null) {
          return String(value)
        }
        const index = binning.edges.findIndex((edge, edgeIndex) =>
          isInContinuousBin(numeric, edge, edgeIndex === lastIndex),
        )
        return index >= 0 ? labels[index] : String(value)
      }
    }
  }

  return (value) => String(value)
}
